import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _find from 'lodash/find';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isAdminSelector } from 'selectors/login-selector';
import { getUserCountryCode } from 'helper/user';
import shopifyLogo from 'public/images/shopify-logo.png';
import {
  shopifyShopsSelector,
  shopifyShopSelector,
} from 'selectors/shopify-selectors';
import { serviceTypesInCitySelector } from 'selectors/booking-selectors';
import { userInfoSelector } from 'selectors/app-selector';
import {
  fetchShopifyShopsAction,
  setShopifyMsdOrderAction,
  setShopifyMultipleOrdersAction,
  clearShopifyOrdersAction,
  clearShopifyBookAgainAction,
  updateShopifyMultipleOrdersQuotesBeforeCreateBookingAction,
  updateShopifyMSDOrderQuoteBeforeCreateBookingAction,
} from 'actions/shopify-actions';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import BackButtonModal from 'components/v2/back-button-modal/back-button-modal';
import ContextualOnboarding, {
  onboardingCounterKey,
} from 'components/v2/contextual-onboarding';
import ShopSelector from './shop-selector';
import ShopifyOrders from './shopify-orders';
import ShopSettingsButton from './shop-settings-button';
import { SHOPIFY_MODE } from './constants.js';
import ShopifyCreateBooking from './shopify-create-booking';
import { isPaxUser } from '../../../helper/grab-id-auth-helper';
import ShopifyTrackEventContext from './track-event';
import { SetupShopModal } from './common';
import ShopifyNew from './shopify-new';
import styles from './styles.less';
import VerifyPickupMatchedOnboarding from './verify-pickup-matched-onboarding';

const cx = classnames.bind(styles);

class ShopifyHome extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: SHOPIFY_MODE.SHOPIFY_ORDERS,
      backButtonModalVisible: false,
    };
  }

  componentDidMount() {
    this.loadShopifyShops();
    window.addEventListener('beforeunload', this.onbeforeunload);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onbeforeunload);
  }

  onbeforeunload = e => {
    e.preventDefault();
    const { mode } = this.state;
    if (
      mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD ||
      mode === SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE
    ) {
      e.returnValue = '';
    }
  };

  fireShopifyTrackEvent = ({ stateName, eventName, facets = {} }) => {
    const { userInfo, shop } = this.props;
    const shopifyCommonFacets = {
      ...getCommonFacet(userInfo),
    };
    if (shop) {
      shopifyCommonFacets[SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_NAME] = shop.name;
      shopifyCommonFacets[SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_ID] = shop.shop;
    }

    scribeTrackEvent({
      stateName,
      eventName,
      facets: {
        ...shopifyCommonFacets,
        ...facets,
      },
    });
  };

  loadShopifyShops = () => {
    const { shops, fetchShopifyShops, userInfo } = this.props;
    if (!shops) {
      fetchShopifyShops({
        countryCode: getUserCountryCode(userInfo),
      });
    }
  };

  onSelectBooking = (mode, data) => {
    if (mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD) {
      this.props.setShopifyMsdOrder(data);
      this.props.updateShopifyMSDOrderQuoteBeforeCreateBooking(data);
    }

    if (mode === SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE) {
      this.props.setShopifyMultipleOrders(data);
      this.props.updateShopifyMultipleOrdersQuotesBeforeCreateBooking(data);
    }

    if (this.state.mode !== mode) {
      this.setState({ mode });
    }
  };

  onClickBack = () => {
    this.setState({
      backButtonModalVisible: true,
    });
  };

  onBackCancel = () => {
    this.setState({ backButtonModalVisible: false });
  };

  onBackConfirm = () => {
    const { clearShopifyOrders } = this.props;
    clearShopifyOrders();
    this.setState({
      backButtonModalVisible: false,
      mode: SHOPIFY_MODE.SHOPIFY_ORDERS,
    });
  };

  onBookingCreateComplete = () => {
    this.setState({
      mode: SHOPIFY_MODE.SHOPIFY_ORDERS,
    });
    this.props.clearShopifyOrders();
  };

  handleChangeToMSD = data => {
    this.props.setShopifyMultipleOrders([]);
    this.props.setShopifyMsdOrder(data);
    this.setState({
      mode: SHOPIFY_MODE.SHOPIFY_BOOK_MSD,
    });
  };

  handleVerifyTipOnboarding = ({ shouldShowAdminContent, shop }) => {
    if (!shouldShowAdminContent || !shop) return;
    const verifyPickupOnboardingID = 'shopifyVerifyPickup';
    if (
      !_find(_get(shop, 'pickUpAddresses', []), x => !x.poiID || !x.grabPoi)
    ) {
      return ReactDOM.createPortal(
        <ContextualOnboarding
          elementID={'shopifyShopSettingsButton'}
          onboardingID={verifyPickupOnboardingID}
          onboardingVisual={<VerifyPickupMatchedOnboarding />}
          onboardingVisualPlacement={'bottomRight'}
          maxOnboardingTimes={1}
          delay={500}
        />,
        document.getElementById('root'),
      );
    }
    window.localStorage.setItem(
      onboardingCounterKey(verifyPickupOnboardingID),
      1,
    );
  };

  render() {
    const { mode, backButtonModalVisible } = this.state;
    const {
      shops,
      shop,
      userInfo,
      isUserAdmin,
      serviceTypesInCity,
      bookAgainData,
      clearBookAgainData,
    } = this.props;
    const isCreatingBooking =
      mode === SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE ||
      mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD;

    const hasNoShops = shops && shops.length === 0;
    const shouldShowAdminContent = isPaxUser() || isUserAdmin;

    return (
      <ShopifyTrackEventContext.Provider
        value={{
          fireShopifyTrackEvent: this.fireShopifyTrackEvent,
        }}
      >
        <div>
          <div className={styles.header}>
            <div className={styles.leftOps}>
              <img
                className={styles.shopifyLogo}
                src={shopifyLogo}
                alt={'shopifyLogo'}
              />
              <span
                className={cx(styles.shopify, {
                  [styles.hideRightBorder]: hasNoShops,
                })}
              >
                Shopify
              </span>
              <ShopSelector />
            </div>
            <div id="shopifyShopSettingsButton" className={styles.rightOps}>
              {shouldShowAdminContent &&
                shop && <ShopSettingsButton shopConfiguration={shop} />}
            </div>
          </div>
          {hasNoShops && <ShopifyNew />}
          {mode === SHOPIFY_MODE.SHOPIFY_ORDERS &&
            shop && (
              <ShopifyOrders
                key={shop && shop.shop}
                shop={shop}
                shops={shops}
                userInfo={userInfo}
                serviceTypesInCity={serviceTypesInCity}
                onBookMultiple={data => {
                  this.onSelectBooking(
                    SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE,
                    data,
                  );
                }}
                onBookMSD={data => {
                  this.onSelectBooking(SHOPIFY_MODE.SHOPIFY_BOOK_MSD, data);
                }}
                bookAgainData={bookAgainData}
                clearBookAgainData={clearBookAgainData}
              />
            )}
          {isCreatingBooking && (
            <ShopifyCreateBooking
              mode={mode}
              userInfo={userInfo}
              onClickBack={this.onClickBack}
              onBookingCreateComplete={this.onBookingCreateComplete}
              onChangeToMSD={this.handleChangeToMSD}
            />
          )}
          <BackButtonModal
            visible={backButtonModalVisible}
            onOk={this.onBackConfirm}
            onCancel={this.onBackCancel}
          />
          {this.handleVerifyTipOnboarding({ shouldShowAdminContent, shop })}
        </div>
        {shouldShowAdminContent && shop && <SetupShopModal shop={shop} />}
      </ShopifyTrackEventContext.Provider>
    );
  }
}

ShopifyHome.propTypes = {
  serviceTypesInCity: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  userInfo: PropTypes.shape({}).isRequired,
  isUserAdmin: PropTypes.bool.isRequired,
  shops: PropTypes.arrayOf(PropTypes.shape({})),
  shop: PropTypes.shape({}),
  fetchShopifyShops: PropTypes.func.isRequired,
  setShopifyMsdOrder: PropTypes.func.isRequired,
  setShopifyMultipleOrders: PropTypes.func.isRequired,
  clearShopifyOrders: PropTypes.func.isRequired,
  bookAgainData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  clearBookAgainData: PropTypes.func.isRequired,
  updateShopifyMultipleOrdersQuotesBeforeCreateBooking:
    PropTypes.func.isRequired,
  updateShopifyMSDOrderQuoteBeforeCreateBooking: PropTypes.func.isRequired,
};

ShopifyHome.defaultProps = {
  shops: null,
  shop: null,
};

const mapDispatchToProps = {
  setShopifyMsdOrder: setShopifyMsdOrderAction,
  setShopifyMultipleOrders: setShopifyMultipleOrdersAction,
  fetchShopifyShops: fetchShopifyShopsAction,
  clearShopifyOrders: clearShopifyOrdersAction,
  clearBookAgainData: clearShopifyBookAgainAction,
  updateShopifyMultipleOrdersQuotesBeforeCreateBooking: updateShopifyMultipleOrdersQuotesBeforeCreateBookingAction,
  updateShopifyMSDOrderQuoteBeforeCreateBooking: updateShopifyMSDOrderQuoteBeforeCreateBookingAction,
};

export default connect(
  state => ({
    userInfo: userInfoSelector(state),
    isUserAdmin: isAdminSelector(state),
    shops: shopifyShopsSelector(state),
    shop: shopifyShopSelector(state),
    serviceTypesInCity: serviceTypesInCitySelector(state),
    bookAgainData: state.shopify.bookAgain,
  }),
  mapDispatchToProps,
)(ShopifyHome);
